// Tutorial-mode orchestrator. Owns the lessons rail, the current
// lesson state, and the run/check workflow. Editor + tab management
// live in main.ts; this module only owns the side panel and the
// lesson-specific buttons in the toolbar. Lessons open through the
// `openLessonInTab` callback so they slot into the tab bar like any
// other document.

import { register as registerCommand } from './commands';
import {
  checkLesson,
  loadLesson,
  loadManifest,
  type Lesson,
  type LessonRef,
  type Manifest,
  type SliceManifest,
} from './lessons/index';
import { type OutputPanel } from './runner';
import type { Kolilang } from './wasm';

const PROGRESS_KEY = 'kolistat:lesson-progress';
const ACTIVE_KEY = 'kolistat:lesson-active';

type ProgressState = 'untouched' | 'attempted' | 'passed';

interface TutorialOptions {
  rail: HTMLElement;
  output: OutputPanel;
  kolilang: Kolilang;
  /// Called when the user activates a lesson — main.ts pushes the
  /// lesson source into a dedicated `lesson:<id>` tab and focuses it.
  openLessonInTab(id: string, title: string, source: string): void;
  /// Run whatever's currently in the active editor tab — main.ts'
  /// onRun() handles selection-vs-whole-doc semantics.
  runActive(): Promise<void>;
  setStatus(text: string): void;
}

interface TutorialApi {
  open(ref: LessonRef): Promise<void>;
  /// Open the last-active lesson (or the first one). Used by the
  /// tutorial entry points in main.ts (toast / palette / Learn) so
  /// they don't have to reach into the manifest themselves.
  openFirst(): Promise<void>;
  /// Sync the rail highlight + lesson panel to whichever tab is now
  /// active. Pass the tab id (e.g. `lesson:01-hello-data`); a non-
  /// lesson id (or null) clears the rail. Side-effect-free: no
  /// output reset, no tab open.
  notifyActiveTab(tabId: string | null): void;
}

const LESSON_TAB_PREFIX = 'lesson:';

export async function attachTutorial(opts: TutorialOptions): Promise<TutorialApi> {
  let manifest: Manifest;
  try {
    manifest = await loadManifest();
  } catch (e) {
    opts.rail.innerHTML = `<p class="lesson-error">Failed to load lesson manifest: ${
      e instanceof Error ? e.message : String(e)
    }</p>`;
    throw e;
  }

  const progress = loadProgress();
  let activeLesson: Lesson | null = null;
  let activeRef: LessonRef | null = null;
  const refIndex = flattenRefs(manifest);

  renderRail(opts.rail, manifest, progress, (ref) => void open(ref));

  function rerender(lesson: Lesson): void {
    const idx = activeRef ? refIndex.findIndex((r) => r.id === activeRef!.id) : -1;
    renderActiveLesson(opts.rail, manifest, progress, lesson, {
      onRun: () => void onRun(),
      onCheck: () => void onCheck(),
      onPrev: () => void navigate(-1),
      onNext: () => void navigate(+1),
      onOpen: (r) => void open(r),
      canPrev: idx > 0,
      canNext: idx >= 0 && idx < refIndex.length - 1,
    });
  }

  async function open(ref: LessonRef): Promise<void> {
    activeRef = ref;
    localStorage.setItem(ACTIVE_KEY, ref.id);
    try {
      const lesson = await loadLesson(ref);
      activeLesson = lesson;
      rerender(lesson);
      // Push the lesson into the dedicated lesson tab. main.ts owns
      // tabs; opening the same lesson twice focuses the existing
      // tab, opening a different one swaps in fresh content.
      opts.openLessonInTab(ref.id, lesson.title, lesson.starter);
      opts.output.clear();
      opts.output.log(`opened: ${lesson.title}`);
      opts.setStatus(`lesson · ${lesson.title}`);
    } catch (e) {
      opts.output.error(
        `failed to load lesson: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }

  async function onRun(): Promise<void> {
    if (!activeLesson) return;
    // Delegate to main.ts so selection-vs-whole-doc semantics match
    // the toolbar Run button + Mod-Enter keymap.
    await opts.runActive();
  }

  async function onCheck(): Promise<void> {
    if (!activeLesson || !activeRef) return;
    if (!activeLesson.expectedCsv || !activeLesson.target) {
      opts.output.log('NOTE: this lesson has no automated check.');
      return;
    }
    try {
      const result = await checkLesson(activeLesson);
      if (!result) {
        opts.output.log('NOTE: nothing to check.');
        return;
      }
      if (result.ok) {
        opts.output.log('CHECK: ✓ matches expected output');
        progress[activeRef.id] = 'passed';
      } else {
        opts.output.error(`CHECK: ✗ ${result.diffs.length} difference(s):`);
        for (const d of result.diffs) opts.output.error(`  ${d.message}`);
        if (progress[activeRef.id] !== 'passed') {
          progress[activeRef.id] = 'attempted';
        }
      }
      saveProgress(progress);
      rerender(activeLesson);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // The "table doesn't exist" failure is the common
      // never-ran-the-lesson case — DuckDB's raw error includes
      // the failing SQL + a misleading pg_catalog suggestion which
      // overwhelms the actually-useful prompt. Detect it and show
      // a clean one-liner instead.
      const targetMissing =
        /Catalog Error.*does not exist/i.test(msg)
        || /Table with name .* does not exist/i.test(msg);
      if (targetMissing) {
        opts.output.error(
          `CHECK: target dataset \`${activeLesson.target ?? '?'}\` doesn't exist yet — click ▶ run first to populate it.`,
        );
      } else {
        opts.output.error(`CHECK failed: ${msg}`);
      }
    }
  }

  async function navigate(delta: number): Promise<void> {
    if (!activeRef) return;
    const idx = refIndex.findIndex((r) => r.id === activeRef!.id);
    const next = refIndex[idx + delta];
    if (next) await open(next);
  }

  registerCommand({
    id: 'lesson-run',
    label: 'Tutorial · Run current lesson',
    shortcut: 'Ctrl+Enter',
    run: () => onRun(),
  });
  registerCommand({
    id: 'lesson-check',
    label: 'Tutorial · Check current lesson',
    shortcut: 'Ctrl+Shift+Enter',
    run: () => onCheck(),
  });
  registerCommand({
    id: 'lesson-next',
    label: 'Tutorial · Next lesson',
    run: () => navigate(+1),
  });
  registerCommand({
    id: 'lesson-prev',
    label: 'Tutorial · Previous lesson',
    run: () => navigate(-1),
  });

  // Ctrl+Enter is owned by the toolbar (its keydown listener clicks
  // the global Run button). Tutorial only adds shortcuts the toolbar
  // doesn't claim.
  document.addEventListener('keydown', (ev) => {
    if (ev.ctrlKey && ev.shiftKey && ev.key === 'Enter') {
      ev.preventDefault();
      void onCheck();
    } else if (ev.altKey && ev.key === 'ArrowRight') {
      ev.preventDefault();
      void navigate(+1);
    } else if (ev.altKey && ev.key === 'ArrowLeft') {
      ev.preventDefault();
      void navigate(-1);
    }
  });

  // Note: no lesson is auto-opened on boot. Returning users who had a
  // lesson tab open get it back via tabs.restore() + notifyActiveTab
  // (wired in main.ts); a fresh load stays scratch-only. The rail's
  // "last lesson" highlight still survives because ACTIVE_KEY is
  // written on explicit opens.
  async function openFirst(): Promise<void> {
    const initial =
      refIndex.find((r) => r.id === localStorage.getItem(ACTIVE_KEY)) ?? refIndex[0];
    if (initial) await open(initial);
  }

  function notifyActiveTab(tabId: string | null): void {
    const lessonId = tabId?.startsWith(LESSON_TAB_PREFIX)
      ? tabId.slice(LESSON_TAB_PREFIX.length)
      : null;
    if (lessonId === (activeRef?.id ?? null)) return;
    if (!lessonId) {
      // Active tab isn't a lesson — drop the panel and clear the
      // rail highlight so the rail mirrors the tab bar.
      activeRef = null;
      activeLesson = null;
      renderRail(opts.rail, manifest, progress, (ref) => void open(ref));
      return;
    }
    const ref = refIndex.find((r) => r.id === lessonId);
    if (!ref) return;
    activeRef = ref;
    localStorage.setItem(ACTIVE_KEY, ref.id);
    void loadLesson(ref).then((lesson) => {
      // Guard against rapid tab switches: only commit if this is
      // still the active ref by the time loadLesson resolves.
      if (activeRef?.id !== ref.id) return;
      activeLesson = lesson;
      rerender(lesson);
      opts.setStatus(`lesson · ${lesson.title}`);
    });
  }

  return { open, openFirst, notifyActiveTab };
}

function flattenRefs(manifest: Manifest): LessonRef[] {
  const out: LessonRef[] = [];
  for (const slice of manifest.slices) {
    for (const lesson of slice.lessons) out.push(lesson);
  }
  return out;
}

function loadProgress(): Record<string, ProgressState> {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { v: number; data: Record<string, ProgressState> };
    if (parsed.v !== 1) return {};
    return parsed.data ?? {};
  } catch {
    return {};
  }
}

function saveProgress(data: Record<string, ProgressState>): void {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify({ v: 1, data }));
}

function renderRail(
  rail: HTMLElement,
  manifest: Manifest,
  progress: Record<string, ProgressState>,
  onOpen: (ref: LessonRef) => void,
): void {
  rail.innerHTML = '';
  rail.appendChild(railHeader());
  // Slice list lives in its own scroll container so the lesson body
  // below can scroll independently without taking the navigator
  // with it.
  const nav = document.createElement('nav');
  nav.className = 'rail-nav';
  for (const slice of manifest.slices) {
    nav.appendChild(renderSlice(slice, progress, onOpen));
  }
  rail.appendChild(nav);
}

function railHeader(): HTMLElement {
  const h = document.createElement('div');
  h.className = 'rail-header';
  h.innerHTML = '<span class="rail-title">lessons</span>';
  return h;
}

function renderSlice(
  slice: SliceManifest,
  progress: Record<string, ProgressState>,
  onOpen: (ref: LessonRef) => void,
): HTMLElement {
  const wrap = document.createElement('section');
  wrap.className = 'rail-slice';
  const title = document.createElement('h3');
  title.className = 'rail-slice-title';
  title.textContent = slice.title;
  wrap.appendChild(title);
  const list = document.createElement('ul');
  list.className = 'rail-list';
  for (const ref of slice.lessons) {
    const li = document.createElement('li');
    li.className = 'rail-item';
    li.dataset.lessonId = ref.id;
    const dot = document.createElement('span');
    dot.className = `rail-dot rail-dot-${progress[ref.id] ?? 'untouched'}`;
    dot.textContent = progress[ref.id] === 'passed' ? '●' : '○';
    li.appendChild(dot);
    const label = document.createElement('span');
    label.className = 'rail-label';
    label.textContent = ref.title;
    li.appendChild(label);
    li.addEventListener('click', () => onOpen(ref));
    list.appendChild(li);
  }
  wrap.appendChild(list);
  return wrap;
}

interface ActionHandlers {
  onRun: () => void;
  onCheck: () => void;
  onPrev: () => void;
  onNext: () => void;
  onOpen: (ref: LessonRef) => void;
  canPrev: boolean;
  canNext: boolean;
}

function renderActiveLesson(
  rail: HTMLElement,
  manifest: Manifest,
  progress: Record<string, ProgressState>,
  lesson: Lesson,
  actions: ActionHandlers,
): void {
  // Refresh the rail so progress dots reflect the latest state, then
  // mount the active-lesson panel below it. Rail items route through
  // the same onOpen callback the panel's prev/next buttons use.
  renderRail(rail, manifest, progress, actions.onOpen);
  // Mark the active item.
  const active = rail.querySelector(`.rail-item[data-lesson-id="${lesson.id}"]`);
  active?.classList.add('rail-item-active');

  const panel = document.createElement('article');
  panel.className = 'lesson-panel';
  const h = document.createElement('h2');
  h.className = 'lesson-title';
  h.textContent = lesson.title;
  panel.appendChild(h);

  const body = document.createElement('div');
  body.className = 'lesson-body';
  body.innerHTML = lesson.bodyHtml;
  panel.appendChild(body);

  const buttons = document.createElement('div');
  buttons.className = 'lesson-actions';
  buttons.appendChild(makeBtn('▶ run', 'btn btn-primary', actions.onRun));
  if (lesson.expectedCsv && lesson.target) {
    buttons.appendChild(makeBtn('✓ check', 'btn', actions.onCheck));
  }
  const prevBtn = makeBtn('← prev', 'btn', actions.onPrev);
  prevBtn.disabled = !actions.canPrev;
  if (!actions.canPrev) prevBtn.title = 'This is the first lesson.';
  buttons.appendChild(prevBtn);
  const nextBtn = makeBtn('next →', 'btn', actions.onNext);
  nextBtn.disabled = !actions.canNext;
  if (!actions.canNext) nextBtn.title = 'This is the last lesson.';
  buttons.appendChild(nextBtn);
  panel.appendChild(buttons);

  rail.appendChild(panel);
}

function makeBtn(label: string, cls: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = cls;
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}
